import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { COPY } from "@/config/copy";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DiscoveryForm({ assessmentId, offerName, onSubmitted }) {
  const d = COPY.discovery;
  const f = d.fields;

  const [form, setForm] = useState({
    project_brief: "",
    role: "",
    instagram: "",
    how_found: "",
    budget: "",
    timeline: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const budgetOptions = d.budget_options[offerName] || d.budget_options["Brand DNA"];
  const timelineOptions = d.timeline_options;

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const pick = (k, v) => setForm({ ...form, [k]: v });

  const valid =
    form.project_brief.trim().length >= 5 &&
    form.role.trim().length > 1 &&
    form.budget &&
    form.timeline;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/assessment/${assessmentId}/discovery`, form);
      setSubmitted(true);
      onSubmitted?.();
      toast.success(d.toast_success);
    } catch (err) {
      const msg = err?.response?.data?.detail || d.toast_error;
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mt-16 text-center max-w-xl mx-auto"
        data-testid="discovery-success"
      >
        <div className="inline-flex items-center gap-3 mb-6">
          <Check size={18} strokeWidth={1.5} style={{ color: "#9fa5a3" }} />
          <div className="section-label" style={{ color: "#9fa5a3" }}>{d.success_eyebrow}</div>
        </div>
        <h4 className="font-serif font-light text-3xl md:text-4xl display-tight mb-4">
          {d.success_headline}
        </h4>
        <p className="text-[15px] text-[#D4D4D4] leading-[1.7]">{d.success_body}</p>
      </motion.div>
    );
  }

  return (
    <motion.form
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      onSubmit={handleSubmit}
      className="mt-16 max-w-xl mx-auto text-left"
      data-testid="discovery-form"
    >
      <div className="section-label mb-8 text-center" style={{ color: "#9fa5a3" }}>
        {d.intro_label}
      </div>

      <div className="space-y-10">
        <Field label={f.project_brief.label} hint={f.project_brief.hint}>
          <textarea
            required rows={4}
            value={form.project_brief}
            onChange={update("project_brief")}
            placeholder={f.project_brief.placeholder}
            className="dark-input"
            data-testid="discovery-brief"
          />
        </Field>

        <Field label={f.role.label}>
          <input type="text" required value={form.role} onChange={update("role")}
            placeholder={f.role.placeholder} className="dark-input" data-testid="discovery-role" />
        </Field>

        <Field label={f.instagram.label}>
          <input type="text" value={form.instagram} onChange={update("instagram")}
            placeholder={f.instagram.placeholder} className="dark-input" data-testid="discovery-instagram" />
        </Field>

        <Field label={f.how_found.label}>
          <input type="text" value={form.how_found} onChange={update("how_found")}
            placeholder={f.how_found.placeholder} className="dark-input" data-testid="discovery-how" />
        </Field>

        <RadioGroup
          label={`${f.budget.label_prefix} ${offerName}`}
          hint={f.budget.hint}
          options={budgetOptions}
          value={form.budget}
          onPick={(v) => pick("budget", v)}
          testidPrefix="discovery-budget"
        />

        <RadioGroup
          label={f.timeline.label}
          options={timelineOptions}
          value={form.timeline}
          onPick={(v) => pick("timeline", v)}
          testidPrefix="discovery-timeline"
        />
      </div>

      <div className="mt-14 text-center">
        <button
          type="submit"
          className="btn-primary-light"
          disabled={!valid || submitting}
          data-testid="discovery-submit-btn"
        >
          {submitting ? d.submit_sending_label : d.submit_label}
          {!submitting && <ArrowRight className="btn-arrow" size={16} strokeWidth={1.5} />}
        </button>
      </div>
    </motion.form>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="section-label mb-2" style={{ color: "#9fa5a3" }}>{label}</div>
      {hint && <div className="text-xs text-[#9fa5a3] mb-3 italic">{hint}</div>}
      {children}
    </div>
  );
}

function RadioGroup({ label, hint, options, value, onPick, testidPrefix }) {
  return (
    <div>
      <div className="section-label mb-2" style={{ color: "#9fa5a3" }}>{label}</div>
      {hint && <div className="text-xs text-[#9fa5a3] mb-4 italic">{hint}</div>}
      <div className="flex flex-col gap-2">
        {options.map((opt) => {
          const active = value === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => onPick(opt.id)}
              data-testid={`${testidPrefix}-${opt.id}`}
              className={`text-left px-5 py-3 rounded-full border transition-all ${
                active
                  ? "border-[#9fa5a3] bg-white/5 text-white"
                  : "border-[#2a2a2a] text-[#D4D4D4] hover:border-[#9fa5a3]"
              }`}
            >
              <span className="inline-flex items-center gap-3">
                <span className={`inline-block w-2 h-2 rounded-full ${active ? "bg-[#9fa5a3]" : "bg-[#3a3a3a]"}`} />
                <span className="text-[15px]">{opt.label}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
